import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { SurveyPage } from './survey/SurveyPage'
import type { Survey } from './survey/survey.types'
import { appConfig } from '../config/app-config'
import { adaptSurveyFromAPI } from '../utils/surveyAdapter'
import { useBrandStore } from '../stores/brandStore'
import { fetchWithRetry, FetchError } from '../utils/fetchWithRetry'
import { logError } from '../utils/rollbar'

export function SurveyLoader() {
  const { surveyId } = useParams<{ surveyId: string }>()
  const navigate = useNavigate()
  const [surveyData, setSurveyData] = useState<Survey | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check for preview mode query parameter once on mount
  const [isPreviewMode] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const isPreview = searchParams.get('preview')?.toLowerCase() === 'true'
    if (isPreview) {
      console.log('🔍 Preview mode detected from URL parameter')
    }
    return isPreview
  })

  const loadBrand = useBrandStore((state) => state.loadBrand)

    const loadSurvey = async () => {
      if (!surveyId) {
        navigate('/')
        return
      }

    try {
        setLoading(true)
        setError(null)

        if (isPreviewMode) {
          console.log('🔍 Preview mode enabled - submissions will be mocked')
        }

        // In preview mode, load from preview subdirectory
        const surveyBasePath = appConfig.r2path.replace(/\/+$/, '')
        const surveyUrl = isPreviewMode
          ? `${surveyBasePath}/preview/${surveyId}.json`
          : `${surveyBasePath}/${surveyId}.json`

        console.log('📥 Loading survey from:', surveyUrl)

        const response = await fetchWithRetry(surveyUrl, {
          maxRetries: 3,
          initialDelay: 1000,
          fetchOptions: {
            cache: 'no-store'
            // Removed Cache-Control header - let browser handle caching policy
            // Facebook's in-app browser may block custom cache headers
          }
        })

        const data = await response.json()

        // Adapt the new API format to the existing internal format
        const adaptedData = adaptSurveyFromAPI(data)
        setSurveyData(adaptedData)

        // Load brand configuration based on survey brand
        const brandId = data.surveyBrand || appConfig.defaultBrandId
        await loadBrand(brandId)
      } catch (error) {
        // Create enhanced error context for debugging on user machines
        const errorContext = {
          surveyId,
          surveyUrl: isPreviewMode
            ? `${appConfig.r2path.replace(/\/+$/, '')}/preview/${surveyId}.json`
            : `${appConfig.r2path.replace(/\/+$/, '')}/${surveyId}.json`,
          isPreviewMode,
          userAgent: navigator.userAgent,
          online: navigator.onLine,
          currentUrl: window.location.href,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          httpStatus: error instanceof FetchError ? error.status : undefined,
          httpStatusText: error instanceof FetchError ? error.statusText : undefined,
          responseHeaders: error instanceof FetchError ? error.headers : undefined,
          // Additional network diagnostics
          connectionType: (navigator as any).connection?.effectiveType,
          connectionDownlink: (navigator as any).connection?.downlink,
          cookiesEnabled: navigator.cookieEnabled,
          doNotTrack: navigator.doNotTrack,
          timestamp: new Date().toISOString()
        }

        logError(`Survey load failed: ${JSON.stringify(errorContext, null, 2)}`, error)

        // Detect if running in Facebook's in-app browser
        const isFacebookBrowser = /FBAN|FBAV/i.test(navigator.userAgent)

        // Check if it's a network error
        const isNetworkError = errorContext.errorMessage === 'Failed to fetch' || errorContext.errorMessage === 'Load failed'

        let errorMsg: string
        if (isFacebookBrowser) {
          errorMsg = 'Unable to load survey in Facebook browser. Please tap the menu (⋯) and select "Open in Browser".'
        } else if (isNetworkError) {
          errorMsg = 'Unable to connect to survey server. This may be due to a firewall or network restriction. Please contact your IT department or try a different network.'
        } else {
          errorMsg = 'Failed to load survey. Please check your connection and try again.'
        }

        setError(errorMsg)
        // Load default brand even on error so retry button uses correct colors
        await loadBrand(appConfig.defaultBrandId)
      } finally {
        setLoading(false)
      }
    }

  useEffect(() => {
    loadSurvey()
  }, [surveyId, navigate, loadBrand])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontSize: '1.5rem',
          color: '#666',
        }}
      >
        Loading survey...
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontSize: '1.5rem',
          color: '#666',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <div>{error}</div>
        <button
          onClick={() => loadSurvey()}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            backgroundColor: 'var(--primary-color, #007bff)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  if (!surveyData) {
    return null
  }

  return <SurveyPage surveyData={surveyData} surveyId={surveyId!} isPreviewMode={isPreviewMode} />
}
